import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { invalidateCache } from '../utils/api_cache';
import api from '../utils/api_client';
import getEnv from '../env';

const API_URL = getEnv('REACT_APP_API_URL') || 'http://localhost:8000';

/**
 * Hook to check if the current user is an admin of an entity.
 * Delegates all logic to the backend admin-check endpoint so that
 * the previous 3–6 sequential requests collapse into a single call.
 * Goes through the API client so the bearer token is attached: the backend
 * takes the caller from the token (auth.py) and answers 401 without one,
 * which this hook would otherwise read as "not an admin".
 *
 * `refresh` re-asks after something changed the answer in this session —
 * leaving a group, say — rather than waiting out the 30s cache.
 *
 * @param {number|string} entityId
 * @returns {{ isAdmin: boolean, loading: boolean, refresh: () => void }}
 */
function useIsAdmin(entityId) {
    const { user } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [version, setVersion] = useState(0);
    const path = `/api/entities/${entityId}/admin-check`;
    // The cache key the client builds for this path, for invalidation.
    const url = entityId && user?.id ? `${API_URL}${path}` : null;

    const refresh = useCallback(() => {
        if (url) invalidateCache(url);
        setVersion(v => v + 1);
    }, [url]);

    useEffect(() => {
        if (!entityId || !user?.id) {
            setIsAdmin(false);
            setLoading(false);
            return;
        }

        if (String(entityId).startsWith('mock-')) {
            setIsAdmin(true);
            setLoading(false);
            return;
        }

        let cancelled = false;
        setLoading(true);

        api.get(path, { cache: true })
            .then(data => {
                if (!cancelled) {
                    setIsAdmin(data.is_admin === true);
                }
            })
            .catch(err => {
                console.error('Error checking admin status:', err);
                if (!cancelled) setIsAdmin(false);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [entityId, user?.id, path, url, version]);

    return { isAdmin, loading, refresh };
}

export default useIsAdmin;
